import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Button, Form, Input } from "antd";
import { useTranslation } from "../../../../utils/onboardingI18n";
import Loader from "../../../../components/Loader";
import {
  addUpdateFinancialInfo,
  getFinancialInfo,
} from "../../../../api/request/corporate";
import { handleApiError } from "../../../../utils/handleApiError";
import {
  CORPORATE_SOURCE_OF_WEALTH_OPTIONS,
  CORPORATE_SOURCE_OF_WEALTH_OTHER,
} from "../../../../utils/onboardingEnums";
import OnboardingMultiSelect from "../../../../components/onboarding/OnboardingMultiSelect.jsx";

const FinancialInfo = ({ onNext, onPrevious, id, isSubmitted }) => {
  const { t } = useTranslation("onBoarding");
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);

  const isDisabled = isSubmitted;
  const sourceOfWealth = Form.useWatch("sourceOfWealth", form);

  const getFinancialInfoData = async () => {
    setIsLoading(true);
    try {
      const { data } = await getFinancialInfo(id);
      if (data?.success) {
        form.setFieldsValue({
          ...data?.data,
          sourceOfWealth: data?.data?.sourceOfWealth || [],
          otherSourceOfWealth: data?.data?.otherSourceOfWealth || "",
        });
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (id) getFinancialInfoData();
  }, [id]);

  const handleSubmit = async (values) => {
    if (isDisabled) return;
    setIsSubmitLoading(true);
    try {
      const payload = {
        ...values,
        sourceOfWealth: values.sourceOfWealth || [],
      };

      if (
        Array.isArray(values.sourceOfWealth) &&
        values.sourceOfWealth.includes(CORPORATE_SOURCE_OF_WEALTH_OTHER) &&
        values.otherSourceOfWealth
      ) {
        payload.otherSourceOfWealth = values.otherSourceOfWealth;
      }

      if (id) Object.assign(payload, { id });

      const { data } = await addUpdateFinancialInfo(payload);

      if (data?.success) {
        toast.success(data?.message);
        onNext();
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsSubmitLoading(false);
    }
  };

  if (isLoading) return <Loader />;

  return (
    <div className="max-w-5xl mx-auto border border-gray-300 rounded-lg p-6 mb-6">
      <Form
        form={form}
        size="large"
        layout="vertical"
        autoComplete="off"
        disabled={isDisabled}
        onFinish={handleSubmit}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Form.Item
            label={t("Net Worth of the Company (USD)")}
            name="companyNetWorth"
            rules={[
              {
                required: true,
                message: t("Please enter net worth of the company"),
              },
            ]}
          >
            <Input type="number" placeholder={t("Enter net worth")} min={1} />
          </Form.Item>

          <Form.Item
            label={t("Approx. annual turnover (USD)")}
            name="annualTurnOver"
            rules={[
              {
                required: true,
                message: t("Please enter approximate annual turnover"),
              },
            ]}
          >
            <Input type="number" placeholder={t("Enter annual turnover")} min={1} />
          </Form.Item>

          <Form.Item
            label={t("Annual Income (USD)")}
            name="annualIncome"
            rules={[{ required: true, message: t("Please enter annual income") }]}
          >
            <Input type="number" placeholder={t("Enter annual income")} min={1} />
          </Form.Item>

          <Form.Item
            label={t("Savings And Investments (USD)")}
            name="savingAndInvestments"
            rules={[
              {
                required: true,
                message: t("Please enter savings and investments"),
              },
            ]}
          >
            <Input
              type="number"
              placeholder={t("Enter savings and investments")}
              min={1}
            />
          </Form.Item>

          <Form.Item
            label={t("Number of Employees")}
            name="noOfEmployees"
            rules={[
              { required: true, message: t("Please enter number of employees") },
            ]}
          >
            <Input
              type="number"
              placeholder={t("Enter number of employees")}
              min={1}
            />
          </Form.Item>

          <Form.Item
            label={t("Approximate Total Assets (USD)")}
            name="approximateTotalAssets"
            rules={[
              {
                required: true,
                message: t("Please enter approximate total assets"),
              },
            ]}
          >
            <Input type="number" placeholder={t("Enter total assets")} min={1} />
          </Form.Item>

          <Form.Item
            label={t("Source of Funds and Wealth")}
            name="sourceOfWealth"
            rules={[
              {
                required: true,
                message: t("Please select source of funds and wealth"),
              },
            ]}
            className="md:col-span-2"
          >
            <OnboardingMultiSelect
              placeholder={t("Select source of funds and wealth")}
              options={CORPORATE_SOURCE_OF_WEALTH_OPTIONS}
            />
          </Form.Item>

          {Array.isArray(sourceOfWealth) &&
            sourceOfWealth.includes(CORPORATE_SOURCE_OF_WEALTH_OTHER) && (
              <Form.Item
                label={t("Please specify other source of funds and wealth")}
                name="otherSourceOfWealth"
                rules={[
                  {
                    required: true,
                    message: t("Please specify other source of funds and wealth"),
                  },
                ]}
                className="md:col-span-2"
              >
                <Input placeholder={t("Enter other source of funds and wealth")} />
              </Form.Item>
            )}
        </div>

        <div className="flex justify-between mt-8">
          <Button onClick={onPrevious}>{t("Previous")}</Button>

          {isDisabled ? (
            <Button type="primary" onClick={onNext} disabled={false}>
              {t("Next")}
            </Button>
          ) : (
            <Button type="primary" htmlType="submit" loading={isSubmitLoading}>
              {isSubmitLoading ? t("Saving...") : t("Next")}
            </Button>
          )}
        </div>
      </Form>
    </div>
  );
};

export default FinancialInfo;
